import { SharedSelection } from '@heroui/react';
import { isEqual } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useContentHeaderContext } from './ContentHeaderContext';
import type { ContentHeaderFilterSelectProps } from './types';
import { useOptionSearch } from './useOptionSearch';

type ResolvedSettings = {
  selectionMode: 'single' | 'multiple';
  autoApply: boolean;
};

const getStandaloneValues = (key: string): string[] => (key ? [key] : []);

export function useFilterSelectState<T extends string = string>(
  props: ContentHeaderFilterSelectProps<T>,
  { selectionMode, autoApply }: ResolvedSettings
) {
  const { options, selectionLimit, onOpen } = props;

  const isStandalone = 'standalone' in props && props.standalone === true;
  const filterKey = isStandalone ? '' : props.filterKey;
  const onSelect = isStandalone ? undefined : props.onSelect;
  const standaloneSelectedKey = isStandalone ? props.selectedKey : ('' as T);
  const standaloneOnChange = isStandalone ? props.onSelectionChange : undefined;

  const { handleSelectFilter, activeOptions, isLoaded } =
    useContentHeaderContext('ContentHeaderFilterSelect');

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { searchTerm, setSearchTerm, visibleOptions } =
    useOptionSearch(options);

  const contextSelectedValues = useMemo(() => {
    if (isStandalone) {
      return getStandaloneValues(standaloneSelectedKey);
    }
    return activeOptions.filters?.[filterKey] || [];
  }, [isStandalone, standaloneSelectedKey, activeOptions.filters, filterKey]);

  const valuesBeforeOpen = useRef<string[]>(contextSelectedValues);
  const [localSelectedValues, setLocalSelectedValues] = useState<string[]>(
    contextSelectedValues
  );

  useEffect(() => {
    setLocalSelectedValues(contextSelectedValues);
  }, [contextSelectedValues]);

  const notifySelection = useCallback(
    (values: string[]) => {
      handleSelectFilter?.(filterKey, values);
      onSelect?.(values, filterKey, {
        ...activeOptions,
        filters: { ...activeOptions.filters, [filterKey]: values }
      });
    },
    [handleSelectFilter, filterKey, onSelect, activeOptions]
  );

  const commitSelection = useCallback(
    (values: string[]) => {
      if (!isStandalone) notifySelection(values);
    },
    [isStandalone, notifySelection]
  );

  const handleSelectionChange = useCallback(
    (keys: SharedSelection) => {
      if (selectionMode === 'single') {
        const selectedKey = keys.currentKey;
        if (!selectedKey) return;
        setLocalSelectedValues([selectedKey]);
        if (isStandalone) {
          standaloneOnChange?.(selectedKey as T);
          return;
        }
        commitSelection([selectedKey]);
        return;
      }
      const selectedKeys = Array.from(keys, String);
      setLocalSelectedValues(selectedKeys);
      if (autoApply) commitSelection(selectedKeys);
    },
    [
      selectionMode,
      isStandalone,
      standaloneOnChange,
      autoApply,
      commitSelection
    ]
  );

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      setIsDropdownOpen(isOpen);
      if (!isOpen) return;
      onOpen?.();
      setSearchTerm('');
      setLocalSelectedValues(contextSelectedValues);
      valuesBeforeOpen.current = [...contextSelectedValues];
    },
    [onOpen, setSearchTerm, contextSelectedValues]
  );

  const handleClose = useCallback(() => {
    if (autoApply) return;
    if (isEqual(localSelectedValues, valuesBeforeOpen.current)) return;
    if (selectionMode === 'multiple') commitSelection(localSelectedValues);
  }, [autoApply, localSelectedValues, selectionMode, commitSelection]);

  const clearSelection = useCallback(() => {
    setLocalSelectedValues([]);
    if (autoApply) commitSelection([]);
  }, [autoApply, commitSelection]);

  const applySelection = useCallback(() => {
    if (!isStandalone) {
      if (selectionMode === 'multiple') {
        notifySelection(localSelectedValues);
      } else {
        handleSelectFilter?.(filterKey, localSelectedValues);
      }
    }
    setIsDropdownOpen(false);
  }, [
    isStandalone,
    selectionMode,
    notifySelection,
    localSelectedValues,
    handleSelectFilter,
    filterKey
  ]);

  const limitReached =
    !!selectionLimit && localSelectedValues.length >= selectionLimit;

  const disabledKeys =
    limitReached && selectionMode === 'multiple'
      ? options
          .filter(option => !localSelectedValues.includes(option.value))
          .map(option => option.value)
      : [];

  const isNonDefaultSelected =
    selectionMode === 'multiple'
      ? localSelectedValues.length > 0
      : localSelectedValues.length > 0 &&
        localSelectedValues[0] !== options[0]?.value;

  return {
    isStandalone,
    isLoaded,
    isDropdownOpen,
    handleOpenChange,
    handleClose,
    handleSelectionChange,
    clearSelection,
    applySelection,
    localSelectedValues,
    isNonDefaultSelected,
    disabledKeys,
    searchTerm,
    setSearchTerm,
    visibleOptions
  };
}
